import React from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import { green, red } from "@mui/material/colors";

interface BudgetHealthBarProps {
  budgeted: number;
  spent: number;
  adultMode: boolean;
  /** optional height in px (defaults to 100%) */
  height?: number | string;
  /** When true, always show the bar even if balanced */
  selected?: boolean;
  /** When false, only show budgeted amount, not the full "$X / $Y" string */
  showHint?: boolean;
}

/**
 * Vertical bar that visualises a category's budget vs spent.
 * Bar fill = budgeted, track = spent mark. Turns red when in deficit.
 */
export const BudgetHealthBar: React.FC<BudgetHealthBarProps> = ({
  budgeted,
  spent,
  adultMode,
  height = "100%",
  selected = false,
  showHint = true,
}) => {
  const isBalanced = budgeted === spent;
  const shouldShow = selected || !isBalanced;

  // Determine slider range and colours
  const marks = [
    {
      value: budgeted,
      label: showHint
        ? `${adultMode ? "$" : ""}${budgeted.toLocaleString()} / ${adultMode ? "$" : ""}${spent.toLocaleString()}`
        : `${adultMode ? "$" : ""}${budgeted.toLocaleString()}`,
    },
  ];

  return (
    <Box
      sx={{
        height,
        display: "flex",
        alignItems: "center",
        opacity: shouldShow ? (selected ? 1 : isBalanced ? 0.3 : 1) : 0,
        transition: "opacity 0.1s ease-in-out",
        visibility: shouldShow ? "visible" : "hidden",
      }}
    >
      <Slider
        orientation="vertical"
        size="medium"
        value={budgeted}
        min={0}
        max={Math.max(budgeted, spent)}
        marks={marks}
        disabled
        sx={{
          "& .MuiSlider-thumb": {
            color:
              spent > budgeted
                ? red.A700
                : spent === budgeted
                  ? green.A700
                  : green[800],
          },
          "& .MuiSlider-track": {
            color: spent > budgeted ? red.A400 : green.A400,
            backgroundColor: spent > budgeted ? red.A400 : green.A400,
          },
          "& .MuiSlider-rail": {
            opacity: 0.3,
            backgroundColor: spent > budgeted ? red[900] : green[500],
          },
          "& .MuiSlider-markLabel": {
            left: 0, // anchor to slider's left edge
            transform: "translate(-100%, 50%)", // shift fully to the left with a small gap
            whiteSpace: "nowrap",
            fontSize: "0.75rem",
            color: spent > budgeted ? red.A400 : green.A400,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            padding: "2px 6px",
            borderRadius: "10px",
            display: "inline-block",
            minWidth: "fit-content",
          },
        }}
      />
    </Box>
  );
};
